import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { apiClient } from '../utils/api';
import { RootState } from '../store';

type BulkDeleteI18n = {
  error: string;
  partialSoft: string;
  partialHard: string;
  softSuccess: string;
  hardSuccess: string;
  softTitle: string;
  softMessage: string;
  entityLabel: string;
};

type Options = {
  softDeletePath: string;
  hardDeletePath: string;
  i18n: BulkDeleteI18n;
  onComplete: () => void | Promise<void>;
};

export function useAdminLogBulkDelete({
  softDeletePath,
  hardDeletePath,
  i18n,
  onComplete,
}: Options) {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';

  const [softDeleteModalOpen, setSoftDeleteModalOpen] = useState(false);
  const [hardDeleteModalOpen, setHardDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 6000);
  };

  const confirmBulkSoftDelete = async (ids: string[]) => {
    if (ids.length === 0) {
      showToast(t(i18n.error), 'error');
      return;
    }
    try {
      setDeleteLoading(true);
      const res = await apiClient(
        softDeletePath,
        { method: 'POST', body: JSON.stringify({ ids }) },
        true,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || t(i18n.error));
      const failed = data.data?.failed as Array<{ id: string; message: string }> | undefined;
      const deletedList = (data.data?.deleted as string[] | undefined) ?? [];
      if (deletedList.length === 0) {
        throw new Error(data?.message || t(i18n.error));
      }
      if (failed?.length) {
        showToast(data.message || t(i18n.partialSoft), 'warning');
      } else {
        showToast(t(i18n.softSuccess, { count: deletedList.length }), 'success');
      }
      setSoftDeleteModalOpen(false);
      await onComplete();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t(i18n.error), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmBulkHardDelete = async (ids: string[]) => {
    if (ids.length === 0) {
      showToast(t(i18n.error), 'error');
      return;
    }
    try {
      setDeleteLoading(true);
      const res = await apiClient(
        hardDeletePath,
        { method: 'POST', body: JSON.stringify({ ids }) },
        true,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || t(i18n.error));
      const failed = data.data?.failed as Array<{ id: string; message: string }> | undefined;
      const deletedList = (data.data?.deleted as string[] | undefined) ?? [];
      if (deletedList.length === 0) {
        throw new Error(data?.message || t(i18n.error));
      }
      if (failed?.length) {
        showToast(data.message || t(i18n.partialHard), 'warning');
      } else {
        showToast(t(i18n.hardSuccess, { count: deletedList.length }), 'success');
      }
      setHardDeleteModalOpen(false);
      await onComplete();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t(i18n.error), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  return {
    canHardDelete,
    softDeleteModalOpen,
    setSoftDeleteModalOpen,
    hardDeleteModalOpen,
    setHardDeleteModalOpen,
    deleteLoading,
    toast,
    confirmBulkSoftDelete,
    confirmBulkHardDelete,
    i18n,
  };
}
